<template>
    <div class="voice-settings">
        <h3 class="settings-title">Ses Ayarları</h3>

        <!-- Ses Modeli Seçimi -->
        <div class="settings-section">
            <h4 class="section-title">Voice Model</h4>
            <div class="voice-model-options">
                <div
                    v-for="model in voiceModels"
                    :key="model.id"
                    class="voice-model-option"
                    :class="{ selected: localVoiceModel === model.id }"
                    @click="selectVoiceModel(model.id)"
                >
                    <div class="radio-button">
                        <div class="radio-dot" v-if="localVoiceModel === model.id"></div>
                    </div>
                    <div class="model-info">
                        <div class="model-name">{{ model.name }}</div>
                        <div class="model-description">{{ model.description }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Ses Sıcaklığı (Temperature) -->
        <div class="settings-section">
            <div class="slider-header">
                <h4 class="section-title">Voice Temperature</h4>
                <span class="value">{{ localVoiceTemperature.toFixed(2) }}</span>
            </div>
            <a-slider v-model="localVoiceTemperature" :min="0" :max="2" :step="0.01" />
            <div class="slider-description">
                Düşük değer daha kararlı ses, yüksek değer daha değişken konuşma üretir. [0-2]
            </div>
        </div>

        <!-- Ses Hızı (Speed) -->
        <div class="settings-section">
            <div class="slider-header">
                <h4 class="section-title">Voice Speed</h4>
                <span class="value">{{ localVoiceSpeed.toFixed(2) }}</span>
            </div>
            <a-slider v-model="localVoiceSpeed" :min="0.5" :max="2" :step="0.01" />
            <div class="slider-description">
                Düşük değer daha yavaş konuşma, yüksek değer daha hızlı konuşma hızı. [0.5-2]
            </div>
        </div>

        <!-- Ses Seviyesi (Volume) -->
        <div class="settings-section">
            <div class="slider-header">
                <h4 class="section-title">Voice Volume</h4>
                <span class="value">{{ localVoiceVolume.toFixed(2) }}</span>
            </div>
            <a-slider v-model="localVoiceVolume" :min="0" :max="2" :step="0.01" />
            <div class="slider-description">
                Düşük değer daha sessiz konuşma, yüksek değer daha yüksek sesli konuşma. [0-2]
            </div>
        </div>

        <!-- Butonlar -->
        <div class="action-buttons">
            <a-button @click="resetSettings">İptal</a-button>
            <PermissionController :permissions="['voice-agent.update']">
                <a-button type="primary" @click="saveSettings">Kaydet</a-button>
            </PermissionController>
        </div>
    </div>
</template>

<script>
export default {
    name: "VoiceSettings",
    props: {
        voiceModel: {
            type: String,
            default: "eleven_multilingual_v2",
        },
        voiceTemperature: {
            type: Number,
            default: 1.0,
        },
        voiceSpeed: {
            type: Number,
            default: 1.0,
        },
        voiceVolume: {
            type: Number,
            default: 1.0,
        },
    },
    data() {
        return {
            localVoiceModel: this.voiceModel,
            localVoiceTemperature: this.voiceTemperature,
            localVoiceSpeed: this.voiceSpeed,
            localVoiceVolume: this.voiceVolume,
            originalValues: {
                voiceModel: this.voiceModel,
                voiceTemperature: this.voiceTemperature,
                voiceSpeed: this.voiceSpeed,
                voiceVolume: this.voiceVolume,
            },
            voiceModels: [
                {
                    id: "eleven_multilingual_v2",
                    name: "Auto(Elevenlabs Multilingual v2)",
                    description: "Multilingual, fast, high quality",
                },
                {
                    id: "eleven_turbo_v2_5",
                    name: "Elevenlabs Turbo V2.5",
                    description: "Multilingual, fast, high quality",
                },
                {
                    id: "eleven_flash_v2_5",
                    name: "Elevenlabs Flash V2.5",
                    description: "Multilingual, fastest, medium quality",
                },
                {
                    id: "eleven_multilingual_v2",
                    name: "Elevenlabs Multilingual v2",
                    description: "Multilingual, slow, highest quality",
                },
            ],
        };
    },
    watch: {
        voiceModel(newValue) {
            this.localVoiceModel = newValue;
            this.originalValues.voiceModel = newValue;
        },
        voiceTemperature(newValue) {
            this.localVoiceTemperature = newValue;
            this.originalValues.voiceTemperature = newValue;
        },
        voiceSpeed(newValue) {
            this.localVoiceSpeed = newValue;
            this.originalValues.voiceSpeed = newValue;
        },
        voiceVolume(newValue) {
            this.localVoiceVolume = newValue;
            this.originalValues.voiceVolume = newValue;
        },
    },
    methods: {
        selectVoiceModel(modelId) {
            this.localVoiceModel = modelId;
        },
        resetSettings() {
            // Değerleri orijinal haline geri döndür
            this.localVoiceModel = this.originalValues.voiceModel;
            this.localVoiceTemperature = this.originalValues.voiceTemperature;
            this.localVoiceSpeed = this.originalValues.voiceSpeed;
            this.localVoiceVolume = this.originalValues.voiceVolume;

            // Popover'ı kapat
            this.$emit("close");
        },
        saveSettings() {
            // Değerleri güncelle
            this.$emit("update", {
                voiceModel: this.localVoiceModel,
                voiceTemperature: this.localVoiceTemperature,
                voiceSpeed: this.localVoiceSpeed,
                voiceVolume: this.localVoiceVolume,
            });

            // Orijinal değerleri güncelle
            this.originalValues = {
                voiceModel: this.localVoiceModel,
                voiceTemperature: this.localVoiceTemperature,
                voiceSpeed: this.localVoiceSpeed,
                voiceVolume: this.localVoiceVolume,
            };

            // Popover'ı kapat
            this.$emit("close");
        },
    },
};
</script>

<style scoped lang="scss">
.voice-settings {
    padding: 15px;

    .settings-title {
        margin: 0 0 15px 0;
        font-size: 16px;
        font-weight: 500;
    }

    .settings-section {
        margin-bottom: 20px;

        .section-title {
            margin: 0 0 10px 0;
            font-size: 14px;
            font-weight: 500;
        }

        .slider-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;

            .section-title {
                margin: 0;
            }

            .value {
                font-weight: 500;
                color: #1890ff;
            }
        }

        .slider-description {
            margin-top: 5px;
            font-size: 12px;
            color: #8c8c8c;
        }

        .voice-model-options {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .voice-model-option {
                display: flex;
                align-items: flex-start;
                padding: 10px;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                cursor: pointer;
                transition: all 0.2s;

                &:hover {
                    border-color: #1890ff;
                }

                &.selected {
                    border-color: #1890ff;
                    background-color: #e6f7ff;
                }

                .radio-button {
                    width: 16px;
                    height: 16px;
                    border: 1px solid #d9d9d9;
                    border-radius: 50%;
                    margin-right: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-top: 2px;

                    .radio-dot {
                        width: 8px;
                        height: 8px;
                        background-color: #1890ff;
                        border-radius: 50%;
                    }
                }

                .model-info {
                    flex: 1;

                    .model-name {
                        font-weight: 500;
                        margin-bottom: 3px;
                    }

                    .model-description {
                        font-size: 12px;
                        color: #8c8c8c;
                    }
                }
            }
        }
    }

    .action-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 15px;
    }
}
</style>
